// react
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'store';
import { dispatch } from 'store';
import { setKindeAccount } from 'store/slices/iamSlice';

// styles
import 'index.css';

// components
import App from 'App';

// hotjar
import Hotjar from '@hotjar/browser';

// sentry
import * as Sentry from '@sentry/react';

// global
import 'globalMethods';

// capacitor
import { Capacitor } from '@capacitor/core';

// polyfills
import './polyfills';

// kinde
import { KindeProvider } from '@kinde-oss/kinde-auth-react';

// router
import { ROUTES } from 'router';

// posthog
import { PostHog } from 'postHog';

const env = import.meta.env.REACT_APP_BUILD_ENVIRONMENT || 'production';

// sentry: requests to these get trace headers. An unset var is left out, as an empty target would match every URL.
const tracePropagationTargets = [
  import.meta.env.REACT_APP_API_URL,
  import.meta.env.REACT_APP_KINDE_ENVIRONMENT_URL &&
    `https://${import.meta.env.REACT_APP_KINDE_ENVIRONMENT_URL}`,
].filter(Boolean);

// hotjar
const siteId =
  {
    dev: 3535435,
    staging: 6537454,
    production: 3612117,
  }[env] || 3535435;
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);

if (
  import.meta.env.REACT_APP_BUILD_ENVIRONMENT &&
  import.meta.env.REACT_APP_BUILD_ENVIRONMENT == 'production'
) {
  Sentry.init({
    dsn: 'https://d7d0f2dc4c5cc660e48177e0c6ef2504@o4508064851755008.ingest.de.sentry.io/4508064856146000',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Requests to these carry Sentry's trace headers. The API starts its own trace; API errors name it in api.traceId.
    tracePropagationTargets,
    profileSessionSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.REACT_APP_BUILD_ENVIRONMENT,
    release: import.meta.env.REACT_APP_BUILD_VERSION,
  });
}

// pwa — register after Sentry so errors are captured
import { registerSW } from 'virtual:pwa-register';
registerSW({ immediate: true });

const redirectUri = Capacitor.isNativePlatform()
  ? `karehero:/${ROUTES.AUTHENTICATE_NATIVE}`
  : `${window.location.origin}${ROUTES.AUTHENTICATE}`;

const logoutUri = Capacitor.isNativePlatform()
  ? `karehero:/${ROUTES.SIGN_IN_NATIVE}`
  : `${window.location.origin}${ROUTES.SIGN_IN}`;

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PostHog>
        <KindeProvider
          audience={import.meta.env.REACT_APP_KINDE_API_AUDIENCE}
          clientId={import.meta.env.REACT_APP_KINDE_TOKEN_CLIENT_ID || ''}
          domain={import.meta.env.REACT_APP_KINDE_TOKEN_DOMAIN || ''}
          logoutUri={logoutUri}
          redirectUri={redirectUri}
          callbacks={{
            onSuccess: (user: any, state: any, context: any) => {
              dispatch(setKindeAccount(user));
            },
            onError: (error: any, state: any, context: any) => {
              console.error('Kinde onError callback', {
                error,
                state,
                context,
              });
            },
          }}
          useInsecureForRefreshToken={
            import.meta.env.NODE_ENV === 'development'
          }
          refreshOnFocus={true}
        >
          <App />
        </KindeProvider>
      </PostHog>
    </Provider>
  </React.StrictMode>,
);
